import type { CommercialFeePolicyWrite } from "@bw/shared/types/generated";
import { useState } from "react";
import {
  Alert,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { asRateFraction } from "@bw/shared/types";
import { fractionToPercent, percentToFraction } from "../contractTermsDraft";

function explicitTimestamp(value: string): number | null {
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/i.test(
      value,
    )
  )
    return null;
  const instant = Date.parse(value);
  return Number.isFinite(instant) ? instant : null;
}

export interface FeePolicyEditorProps {
  value: CommercialFeePolicyWrite | null;
  onChange: (value: CommercialFeePolicyWrite) => void;
  onSave: () => void;
  isPending: boolean;
  error: string | null;
}

/** Explicit draft inputs only; null is incomplete, not a default valid price. */
export function FeePolicyEditor({
  value,
  onChange,
  onSave,
  isPending,
  error,
}: FeePolicyEditorProps) {
  type RuleText = {
    service: string;
    origin: string;
    purpose: string;
    component: string;
    percent: string;
    flat: string;
    cap: string;
    currency: string;
    stacking: string;
  };
  const blank = (): RuleText => ({
    service: "",
    origin: "",
    purpose: "",
    component: "",
    percent: "",
    flat: "",
    cap: "",
    currency: "",
    stacking: "",
  });
  const [audience, setAudience] = useState(
    value ? (value.audience_org_id === null ? "all" : "org") : "",
  );
  const [org, setOrg] = useState(value?.audience_org_id ?? "");
  const [from, setFrom] = useState(value?.effective_from ?? "");
  const [until, setUntil] = useState(value?.effective_until ?? "");
  const [rules, setRules] = useState<RuleText[]>(
    () =>
      value?.rules.map((rule) => ({
        service: rule.selector.service_key,
        origin: rule.selector.origin,
        purpose: rule.selector.purpose,
        component: rule.component,
        percent: fractionToPercent(String(rule.percentage)),
        flat: rule.flat_fee == null ? "" : String(rule.flat_fee.amount_minor),
        cap: rule.cap == null ? "" : String(rule.cap.amount_minor),
        currency: rule.flat_fee?.currency ?? rule.cap?.currency ?? "",
        stacking: rule.campaign_stacking,
      })) ?? [blank()],
  );
  function build(
    nextAudience = audience,
    nextOrg = org,
    nextFrom = from,
    nextUntil = until,
    nextRules = rules,
  ): CommercialFeePolicyWrite | null {
    const startsAt = explicitTimestamp(nextFrom);
    const endsAt = nextUntil ? explicitTimestamp(nextUntil) : null;
    if (
      !nextAudience ||
      startsAt === null ||
      (nextUntil && (endsAt === null || endsAt <= startsAt))
    )
      return null;
    if (
      nextAudience === "org" &&
      !/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(nextOrg)
    )
      return null;
    const result = [];
    for (const rule of nextRules) {
      const fraction = percentToFraction(rule.percent);
      if (
        !rule.service.trim() ||
        !rule.origin ||
        !rule.purpose.trim() ||
        !rule.component ||
        fraction === null ||
        !rule.stacking
      )
        return null;
      if ((rule.flat || rule.cap) && !rule.currency) return null;
      if (
        [rule.flat, rule.cap].some(
          (money) =>
            money &&
            (!/^\d+$/.test(money) || !Number.isSafeInteger(Number(money))),
        )
      )
        return null;
      result.push({
        selector: {
          service_key: rule.service.trim(),
          origin:
            rule.origin as CommercialFeePolicyWrite["rules"][number]["selector"]["origin"],
          purpose: rule.purpose.trim(),
        },
        component:
          rule.component as CommercialFeePolicyWrite["rules"][number]["component"],
        percentage: asRateFraction(fraction)!,
        flat_fee: rule.flat
          ? { amount_minor: Number(rule.flat), currency: rule.currency }
          : null,
        cap: rule.cap
          ? { amount_minor: Number(rule.cap), currency: rule.currency }
          : null,
        waiver_approval_reference:
          value?.rules[result.length]?.waiver_approval_reference ?? null,
        campaign_stacking:
          rule.stacking as CommercialFeePolicyWrite["rules"][number]["campaign_stacking"],
        rounding: "half_up" as const,
      });
    }
    return {
      audience_org_id: nextAudience === "all" ? null : nextOrg,
      effective_from: new Date(startsAt).toISOString(),
      effective_until: endsAt === null ? null : new Date(endsAt).toISOString(),
      rules: result,
    };
  }
  const changed = (a = audience, o = org, f = from, u = until, r = rules) => {
    const next = build(a, o, f, u, r);
    if (next) onChange(next);
  };
  const setRule = (index: number, field: keyof RuleText, text: string) => {
    const next = rules.map((rule, i) =>
      i === index ? { ...rule, [field]: text } : rule,
    );
    setRules(next);
    changed(audience, org, from, until, next);
  };
  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <TextField
        select
        label="Audience"
        size="small"
        value={audience}
        disabled={isPending}
        onChange={(event) => {
          setAudience(event.target.value);
          changed(event.target.value);
        }}
      >
        <MenuItem value="all">All organizations</MenuItem>
        <MenuItem value="org">One organization</MenuItem>
      </TextField>
      {audience === "org" ? (
        <TextField
          label="Organization reference"
          size="small"
          value={org}
          disabled={isPending}
          onChange={(e) => {
            setOrg(e.target.value);
            changed(audience, e.target.value);
          }}
        />
      ) : null}
      <TextField
        label="Effective from (UTC timestamp)"
        helperText="Include Z or an explicit UTC offset."
        size="small"
        value={from}
        disabled={isPending}
        onChange={(e) => {
          setFrom(e.target.value);
          changed(audience, org, e.target.value);
        }}
      />
      <TextField
        label="Effective until (optional UTC timestamp)"
        helperText="Include Z or an explicit UTC offset if provided."
        size="small"
        value={until}
        disabled={isPending}
        onChange={(e) => {
          setUntil(e.target.value);
          changed(audience, org, from, e.target.value);
        }}
      />
      {rules.map((rule, index) => (
        <Stack key={index} spacing={1}>
          <Typography variant="subtitle2">Fee rule {index + 1}</Typography>
          <TextField
            label="Service key"
            size="small"
            value={rule.service}
            disabled={isPending}
            onChange={(e) => setRule(index, "service", e.target.value)}
          />
          <TextField
            label="Purpose"
            size="small"
            value={rule.purpose}
            disabled={isPending}
            onChange={(e) => setRule(index, "purpose", e.target.value)}
          />
          <TextField
            select
            label="Origin"
            size="small"
            value={rule.origin}
            disabled={isPending}
            onChange={(e) => setRule(index, "origin", e.target.value)}
          >
            {[
              "player",
              "guardian",
              "business_operator",
              "system",
              "acquisition",
              "organization_software",
            ].map((item) => (
              <MenuItem key={item} value={item}>
                {item.replaceAll("_", " ")}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Component"
            size="small"
            value={rule.component}
            disabled={isPending}
            onChange={(e) => setRule(index, "component", e.target.value)}
          >
            <MenuItem value="bw_commission">BW commission</MenuItem>
            <MenuItem value="acquisition">Acquisition fee</MenuItem>
          </TextField>
          <TextField
            label="Percentage"
            size="small"
            value={rule.percent}
            disabled={isPending}
            onChange={(e) => setRule(index, "percent", e.target.value)}
          />
          <TextField
            select
            label="Currency for flat fee or cap"
            size="small"
            value={rule.currency}
            disabled={isPending}
            onChange={(e) => setRule(index, "currency", e.target.value)}
          >
            {["aud", "cad", "eur", "usd"].map((currency) => (
              <MenuItem key={currency} value={currency}>
                {currency.toUpperCase()}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Flat fee (optional cents)"
            size="small"
            value={rule.flat}
            disabled={isPending}
            onChange={(e) => setRule(index, "flat", e.target.value)}
          />
          <TextField
            label="Cap (optional cents)"
            size="small"
            value={rule.cap}
            disabled={isPending}
            onChange={(e) => setRule(index, "cap", e.target.value)}
          />
          <TextField
            select
            label="Campaign stacking"
            size="small"
            value={rule.stacking}
            disabled={isPending}
            onChange={(e) => setRule(index, "stacking", e.target.value)}
          >
            <MenuItem value="exclusive">Exclusive</MenuItem>
            <MenuItem value="add_after_commission">
              Added after commission
            </MenuItem>
          </TextField>
          <Typography variant="body2" color="text.secondary">
            Rounding: half up.
          </Typography>
        </Stack>
      ))}
      <Button
        size="small"
        variant="outlined"
        disabled={isPending}
        onClick={() => setRules([...rules, blank()])}
      >
        Add fee rule
      </Button>
      <Button
        size="small"
        variant="contained"
        disabled={isPending || !build() || value === null}
        onClick={() => {
          if (value && build()) onSave();
        }}
      >
        Save draft
      </Button>
    </Stack>
  );
}
